<template>
    <bottom-sheet v-model="shows" v-bind="{ title }" fullscreen persistent width="720" maxWidth="100%">
        <template #activator="bind">
            <slot name="activator" v-bind="bind" />
        </template>
        <v-layout column justify-center class="bottom-sheet__contents h-100">
            <v-sheet style="flex: 1 1 auto; overflow-x: hidden; overflow-y: auto" class="scroll-primary">
                <vertical-form-table v-model="form" v-bind="{ items }" class="mb-20 mb-lg-40 mx-8" @input="emit">
                    <template #[ITEM_TERM_CATEGORY]>
                        <form-category v-model="form" @input="emit" />
                    </template>
                    <template #[ITEM_TERM_CODE]>
                        <form-code v-model="form" @input="emit" />
                    </template>
                    <template #[ITEM_TERM_VIDEO]>
                        <form-video v-model="form" @input="emit" />
                    </template>
                    <template #[ITEM_TERM_LINKS]>
                        <form-links v-model="form" @input="emit" />
                    </template>
                </vertical-form-table>
            </v-sheet>
            <div class="v-btn-group flex-wrap">
                <v-btn outlined color="grey-d" class="min-w-140px min-w-lg-160px" v-bind="{ loading }" @click="shows = false"><span class="grey-6--text">취소하기</span></v-btn>
                <v-btn color="primary" class="min-w-140px min-w-lg-160px" v-bind="{ loading }" @click="save">
                    <template v-if="isCreate">
                        <span> 등록하기 </span>
                    </template>
                    <template v-else>
                        <span> 수정하기 </span>
                    </template>
                </v-btn>
            </div>
        </v-layout>
    </bottom-sheet>
</template>

<script>
import api from "@/api";
import { attrs_input, attrs_switch } from "@/assets/variables/attrs";
import { initLecture } from "@/store/lecture";

import BottomSheet from "@/components/dumb/bottom-sheet.vue";
import VerticalFormTable from "@/components/dumb/vertical-form-table.vue";

import FormCategory from "./form-category.vue";
import FormCode from "./form-code.vue";
import FormLinks from "./form-links.vue";
import FormVideo from "./form-video.vue";

const ITEM_TERM_CATEGORY = "카테고리";
const ITEM_TERM_CODE = "참여코드";
const ITEM_TERM_LINKS = "교육자료(링크)";
const ITEM_TERM_VIDEO = "영상주소";

export default {
    components: {
        BottomSheet,
        VerticalFormTable,

        FormCategory,
        FormCode,
        FormLinks,
        FormVideo,
    },
    props: {
        value: { type: Object, default: initLecture },
    },
    data: () => ({
        form: initLecture(),

        shows: false,
        loading: false,

        ITEM_TERM_CATEGORY,
        ITEM_TERM_CODE,
        ITEM_TERM_LINKS,
        ITEM_TERM_VIDEO,
    }),
    computed: {
        isCreate() {
            return !this.value?._id;
        },
        title() {
            let title = "교육 서비스 영상";
            if (this.isCreate) title += " 등록하기";
            else title += " 수정하기";
            return title;
        },
        items() {
            return [
                {
                    key: "_category",
                    term: ITEM_TERM_CATEGORY,
                },
                {
                    key: "subject",
                    term: "영상 제목",
                    type: "text",
                    ...attrs_input,
                },
                {
                    key: "thumb",
                    term: "썸네일",
                    type: "file",
                    accept: "image/*",
                    label: "권장 해상도 : 512px * 288px",
                    ...attrs_input,
                },
                {
                    key: "code",
                    term: ITEM_TERM_CODE,
                },
                {
                    key: "video",
                    term: ITEM_TERM_VIDEO,
                },
                {
                    key: "files",
                    term: "교육자료(파일)",
                    type: "file-chips",
                    column: true,
                },
                {
                    key: "links",
                    term: ITEM_TERM_LINKS,
                },
                {
                    key: "isStream",
                    term: "실시간",
                    type: "switch",
                    label: this.form.isStream ? "표기" : "비표기",
                    message: "실시간 표기 시 실시간 목록에 노출됩니다.",
                    ...attrs_switch,
                },
                {
                    key: "isPublic",
                    term: "공개여부",
                    type: "switch",
                    label: this.form.isPublic ? "공개" : "비공개",
                    message: "비공개시 마이스펙 홈페이지 노출되지 않습니다.\r\n영상 등록 후 생성되는 URL을 직접 입력시 시청 가능합니다.",
                    ...attrs_switch,
                },
                {
                    key: "isActive",
                    term: "접근가능",
                    type: "switch",
                    label: this.form.isActive ? "가능" : "불가",
                    message: "영상 비활성 시 노출 및 시청 접속이 불가능 합니다.",
                    ...attrs_switch,
                },
            ];
        },
    },
    methods: {
        async init() {
            this.tab = 0;
            this.loading = false;
            this.form = initLecture(this.value);

            if (!this.isCreate) {
                this.loading = true;
                try {
                    const { lecture } = await api.v1.company.lectures.get({ _id: this.value?._id });
                    this.$emit("input", lecture);
                    this.form = initLecture(lecture);
                } finally {
                    this.loading = false;
                }
            }
        },

        async save() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { _thumb, thumb, _files, files, ...form } = this.form;
                let { post, put } = api.v1.company.lectures;
                let { lecture } = await (this.isCreate ? post : put)(form);

                // thumb POST/DELETE
                if (thumb instanceof File) {
                    let index = `${lecture?._id}-thumb`;
                    thumb = (await api.v1.me.files.post({ index }, thumb))?.file;
                }
                if ((thumb?._id || null) != _thumb) {
                    if (_thumb) await api.v1.me.files.delete({ _id: _thumb });
                    _thumb = thumb?._id || null;
                }

                // files POST/DELETE
                files = await Promise.all(
                    files.map(async (file, i) => {
                        if (file instanceof File) {
                            let index = `${lecture?._id}-files-${i}`;
                            file = (await api.v1.me.files.post({ index }, file))?.file;
                        }
                        return file;
                    })
                );
                await Promise.all(
                    _files.map(async (_id) => {
                        if (files.some((item) => item._id == _id)) return;
                        await api.v1.me.files.delete({ _id });
                    })
                );
                _files = files.map(({ _id }) => _id);

                lecture = (await put({ _id: lecture?._id, _thumb, _files }))?.lecture;

                this.form = initLecture(lecture);
                this.$emit("input", lecture);
                this.$emit("search");
                alert(this.isCreate ? "생성되었습니다." : "수정되었습니다.");
                this.shows = false;
            } finally {
                this.loading = false;
            }
        },

        emit() {
            this.form = initLecture(this.form);
        },
    },
    watch: {
        shows() {
            if (this.shows) this.init();
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep {
    .text-right input {
        text-align: right;
    }
    .v-input--switch label {
        margin-bottom: 4px;
    }
    .v-textarea textarea {
        margin: 0 !important;
        padding-top: 8px;
        line-height: 1.875em;
    }
}
</style>
